/*
  THEMERIE: product photo zoom modes (main-product setting image_zoom).
  - gallery_lightbox: PhotoSwipe lightbox, one photo at a time. Pinch, double-tap, swipe between photos,
    swipe down, Esc or the browser's Back button closes it.
  - hover_zoom: on desktop the photo zooms inside its own frame and follows the mouse; a click opens the lightbox.
  - click_zoom: the same zoom after a click (never by accident); the corner icon or Enter opens the lightbox.
  - editorial: Dawn's photo window restyled, all photos below each other with a thumbnail rail (desktop).
  On touch screens every mode uses the lightbox. PhotoSwipe (MIT, assets/photoswipe.*) loads on the first open.
*/
(() => {
  const script = document.currentScript;
  const PSWP_JS = script && script.dataset.pswpJs;
  const PSWP_CSS = script && script.dataset.pswpCss;
  const MODES = ['gallery_lightbox', 'hover_zoom', 'click_zoom', 'editorial'];
  const touch = window.matchMedia('(hover: none), (pointer: coarse)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const icon = (d, w = 20) =>
    `<svg class="pswp__icn" viewBox="0 0 24 24" width="${w}" height="${w}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;

  let pswpLoading = null;
  const loadPhotoSwipe = () => {
    if (pswpLoading) return pswpLoading;
    const css = new Promise((resolve) => {
      if (document.querySelector('link[data-pswp-css]')) return resolve();
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = PSWP_CSS;
      link.dataset.pswpCss = '';
      link.onload = link.onerror = () => resolve();
      document.head.appendChild(link);
    });
    pswpLoading = Promise.all([import(PSWP_JS).then((module) => module.default), css]).then(([PhotoSwipe]) => PhotoSwipe);
    pswpLoading.catch(() => (pswpLoading = null));
    return pswpLoading;
  };

  const zoomItems = (gallery) =>
    Array.from(gallery.querySelectorAll('[id^="GalleryViewer"] .product__media-list > [data-zoom-src]'));

  async function openLightbox(gallery, item) {
    const items = zoomItems(gallery);
    const start = Math.max(0, items.indexOf(item));
    let PhotoSwipe;
    try {
      PhotoSwipe = await loadPhotoSwipe();
    } catch (error) {
      // the lightbox could not load: fall back to Dawn's photo window so the click still does something
      const opener = item.querySelector('.product__media-toggle');
      const modal = opener && document.querySelector(opener.closest('modal-opener').dataset.modal);
      if (modal) modal.show(opener);
      return;
    }
    const desktop = !touch.matches && window.innerWidth >= 750;
    const withThumbs = desktop && gallery.dataset.zoomThumbnails === 'true' && items.length > 1;
    const max = parseFloat(gallery.dataset.zoomMax) || 3;
    const dataSource = items.map((el) => {
      const img = el.querySelector('.product__media img');
      return {
        src: el.dataset.zoomSrc,
        width: Number(el.dataset.zoomWidth),
        height: Number(el.dataset.zoomHeight),
        alt: el.dataset.zoomAlt || '',
        msrc: img ? img.currentSrc || img.src : undefined,
        element: img,
        thumb: el.dataset.zoomSrc.replace(/([?&])width=\d+/, '$1width=160'),
      };
    });

    const pswp = new PhotoSwipe({
      dataSource,
      index: start,
      bgOpacity: 1,
      showHideAnimationType: reduceMotion.matches ? 'none' : 'zoom',
      initialZoomLevel: 'fit',
      secondaryZoomLevel: (zoom) => zoom.fit * Math.min(2.5, max),
      maxZoomLevel: (zoom) => zoom.fit * max,
      wheelToZoom: true,
      closeOnVerticalDrag: true,
      padding: desktop
        ? { top: 72, bottom: withThumbs ? 112 : 56, left: 80, right: 80 }
        : { top: 56, bottom: 24, left: 0, right: 0 },
      mainClass: `pswp--themerie pswp--${gallery.dataset.zoomBackground === 'dark' ? 'dark' : 'light'}`,
      closeTitle: gallery.dataset.labelClose,
      arrowPrevTitle: gallery.dataset.labelPrev,
      arrowNextTitle: gallery.dataset.labelNext,
      indexIndicatorSep: ' / ',
      closeSVG: icon('M6 6l12 12M18 6L6 18'),
      arrowPrevSVG: icon('M15 5l-7 7 7 7'),
      arrowNextSVG: icon('M9 5l7 7-7 7'),
      zoomSVG: icon('M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM15.5 15.5 20 20M8 10.5h5M10.5 8v5'),
      errorMsg: '',
    });

    // a photo that is hidden in the gallery (the carousel shows one at a time) has no box to zoom from: fade instead
    pswp.addFilter('thumbEl', (el) => (el && el.getClientRects().length && el.getBoundingClientRect().width > 0 ? el : null));

    if (withThumbs) {
      pswp.on('uiRegister', () => {
        pswp.ui.registerElement({
          name: 'th-thumbs',
          appendTo: 'wrapper',
          onInit: (el) => {
            el.classList.add('pswp__th-thumbs');
            dataSource.forEach((slide, i) => {
              const button = document.createElement('button');
              button.type = 'button';
              button.className = 'pswp__th-thumb';
              button.setAttribute('aria-label', `${i + 1} ${gallery.dataset.labelOf || '/'} ${dataSource.length}`);
              const img = document.createElement('img');
              img.src = slide.thumb;
              img.alt = '';
              img.loading = 'lazy';
              button.appendChild(img);
              button.addEventListener('click', () => pswp.goTo(i));
              el.appendChild(button);
            });
            const paint = () =>
              Array.from(el.children).forEach((b, i) => b.setAttribute('aria-current', String(i === pswp.currIndex)));
            pswp.on('change', paint);
            paint();
          },
        });
      });
    }

    // the browser's Back button closes the lightbox, as visitors expect from an overlay
    let closedByBack = false;
    const onPop = () => {
      closedByBack = true;
      pswp.close();
    };
    try {
      history.pushState({ thLightbox: true }, '');
      window.addEventListener('popstate', onPop);
    } catch (error) {}
    pswp.on('close', () => {
      window.removeEventListener('popstate', onPop);
      if (!closedByBack && history.state && history.state.thLightbox) history.back();
      // leave the gallery on the photo the visitor ended on
      const last = items[pswp.currIndex];
      if (last && pswp.currIndex !== start && typeof gallery.setActiveMedia === 'function')
        gallery.setActiveMedia(last.dataset.mediaId, false);
    });
    pswp.init();
  }

  // zoom in place: scale the photo inside its own frame towards the pointer
  function setupInPlace(gallery, viewer, mode) {
    const level = parseFloat(gallery.dataset.zoomInplace) || 2.5;
    let timer = null;
    let pointer = null;
    const parts = (item) => ({ frame: item.querySelector('.product__media'), img: item.querySelector('.product__media img') });
    const aim = (item, at) => {
      const { frame, img } = parts(item);
      if (!frame || !img) return;
      const r = frame.getBoundingClientRect();
      img.style.transformOrigin = `${((at.clientX - r.left) / r.width) * 100}% ${((at.clientY - r.top) / r.height) * 100}%`;
    };
    const zoomIn = (item, at) => {
      const { frame, img } = parts(item);
      if (!frame || !img) return;
      // ask the browser for a sharper file from the srcset once the photo is shown larger
      if (!img.dataset.thSizes) {
        img.dataset.thSizes = img.sizes || '';
        img.sizes = `${Math.round(frame.getBoundingClientRect().width * level)}px`;
      }
      aim(item, at);
      item.classList.add('is-zoomed');
      img.style.transform = `scale(${level})`;
    };
    const zoomOut = (item) => {
      const { img } = parts(item);
      item.classList.remove('is-zoomed');
      if (img) img.style.transform = '';
    };
    viewer.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      const item = event.target.closest('[data-zoom-src]');
      if (!item) return;
      pointer = { clientX: event.clientX, clientY: event.clientY };
      if (item.classList.contains('is-zoomed')) return aim(item, pointer);
      // hover: a short delay so passing over the photo on the way to the buy button does not zoom it
      if (mode === 'hover_zoom' && !timer) timer = window.setTimeout(() => zoomIn(item, pointer), 120);
    });
    viewer.addEventListener('pointerout', (event) => {
      const item = event.target.closest('[data-zoom-src]');
      if (!item || item.contains(event.relatedTarget)) return;
      window.clearTimeout(timer);
      timer = null;
      zoomOut(item);
    });
    return (item, event) => (item.classList.contains('is-zoomed') ? zoomOut(item) : zoomIn(item, event));
  }

  // editorial: thumbnail rail in Dawn's photo window, kept in step with the photo in view
  function setupEditorial(modal) {
    const rail = modal.querySelector('.th-modal-rail');
    const content = modal.querySelector('.product-media-modal__content');
    if (!rail || !content) return;
    const target = (button) => content.querySelector(`[data-media-id="${button.dataset.railTarget}"]`);
    const buttons = Array.from(rail.querySelectorAll('[data-rail-target]'));
    buttons.forEach((button) =>
      button.addEventListener('click', () => {
        const el = target(button);
        if (el) el.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
      })
    );
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = entry.target.dataset.mediaId;
          buttons.forEach((b) => b.setAttribute('aria-current', String(b.dataset.railTarget === id)));
        }),
      { root: content, threshold: 0.55 }
    );
    buttons.forEach((button) => {
      const el = target(button);
      if (el) observer.observe(el);
    });
  }

  function setup(gallery) {
    const mode = gallery.dataset.zoom;
    if (!MODES.includes(mode) || gallery.dataset.thZoomReady) return;
    gallery.dataset.thZoomReady = 'true';
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;
    const toggleInPlace = mode === 'hover_zoom' || mode === 'click_zoom' ? setupInPlace(gallery, viewer, mode) : null;

    // fetch the lightbox code as soon as the visitor shows interest, so the first open is instant
    const warm = () => {
      if (mode !== 'editorial' || touch.matches) loadPhotoSwipe().catch(() => {});
    };
    gallery.addEventListener('pointerenter', warm, { once: true });
    gallery.addEventListener('focusin', warm, { once: true });
    gallery.addEventListener('touchstart', warm, { once: true, passive: true });

    // capture phase: decide before Dawn's modal opener sees the click
    viewer.addEventListener(
      'click',
      (event) => {
        const item = event.target.closest('[data-zoom-src]');
        if (!item || !event.target.closest('.product__media-toggle, .product__media-icon')) return;
        if (mode === 'editorial' && !touch.matches) return; // Dawn's window, restyled
        event.preventDefault();
        event.stopPropagation();
        const onIcon = Boolean(event.target.closest('.product__media-icon'));
        if (mode === 'click_zoom' && !touch.matches && event.detail > 0 && !onIcon) return toggleInPlace(item, event);
        openLightbox(gallery, item);
      },
      true
    );

    if (mode === 'editorial') {
      const modal = document.querySelector(`#ProductModal-${gallery.id.replace('MediaGallery-', '')}`);
      if (modal) setupEditorial(modal);
    }
  }

  const init = () => document.querySelectorAll('media-gallery[data-zoom]').forEach(setup);
  init();
  // the theme editor, quick view and a product swap render a new gallery: set that one up too
  document.addEventListener('shopify:section:load', init);
  new MutationObserver(() => {
    if (document.querySelector('media-gallery[data-zoom]:not([data-th-zoom-ready])')) init();
  }).observe(document.body, { childList: true, subtree: true });
})();
